import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import StatePicker, { type MachineStateOption } from './StatePicker.js';

const SELECT_CLASS = 'w-full bg-gray-800 text-white px-4 py-3 rounded border border-gray-600 focus:border-red-500 focus:outline-none disabled:opacity-50';

export default function FermoModal({ states, onConfirm, onClose, title }: {
  states: MachineStateOption[];
  onConfirm: (machineStateId: number, note?: string) => Promise<void> | void;
  onClose: () => void;
  /** Titolo del modale: di default "MOTIVO DEL FERMO", diverso quando si cambia stato in generale */
  title?: string;
}) {
  const { t } = useTranslation();
  const [stateId, setStateId] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleConfirm() {
    if (!stateId) return;
    setSaving(true);
    try {
      await onConfirm(stateId, note.trim() || undefined);
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(10,10,25,0.92)' }}>
      <div className="bg-gray-900 border border-gray-600 rounded-xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-700 bg-red-800">
          <h2 className="text-white font-bold text-lg tracking-wide">{title ?? t('fermo.title')}</h2>
        </div>
        <div className="p-6 space-y-4">
          <StatePicker
            states={states}
            value={stateId}
            onChange={setStateId}
            selectClassName={SELECT_CLASS}
            showLabels
          />

          <div>
            <label className="block text-gray-300 font-bold text-sm mb-2">{t('fermo.note')}</label>
            <textarea
              className="w-full bg-gray-800 text-white px-4 py-3 rounded border border-gray-600 focus:border-red-500 focus:outline-none resize-none"
              rows={2}
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 rounded-lg">
              {t('fermo.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={!stateId || saving}
              className="flex-1 bg-red-700 hover:bg-red-600 disabled:bg-gray-700 text-white font-bold py-3 rounded-lg"
            >
              {saving ? t('fermo.saving') : t('fermo.confirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
